/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package models

import kotlinx.serialization.Serializable

/**
 * Where a goal's money actually lives. Drives which financial framework the
 * Wiz 3R AI applies — e.g. RRSP gets tax-deferral + compound-growth math,
 * HISA gets liquidity coverage math, FHSA gets first-home-purchase math.
 *
 * Nullable on [SavingsGoal] so existing goals don't break — when null, the
 * AI uses conditional phrasing across the realistic options for the goal's
 * [GoalCategory].
 */
@Serializable
enum class GoalAccountType {
    HISA,                   // High-interest savings — liquid cash
    TFSA,                   // Tax-free savings account (cash or invested mode)
    RRSP,                   // Registered retirement savings plan
    FHSA,                   // First-home savings account
    RESP,                   // Registered education savings plan (CESG matching)
    RDSP,                   // Registered disability savings plan
    NON_REGISTERED,         // Taxable brokerage / open account
    EMPLOYER_PENSION,       // Defined benefit / defined contribution / group RRSP
    CHEQUING,               // Day-to-day account (rarely the right home for a goal)
    OTHER,                  // Caller specified something outside the catalog
    ;

    companion object {
        /**
         * Which account types make sense for a given goal category. The web
         * "account type" dropdown filters by this so users see only relevant
         * options instead of the full enum.
         *
         * RETIREMENT, INVESTMENT, HOME_PURCHASE, EDUCATION need real choices.
         * EMERGENCY_FUND / VACATION / MAJOR_PURCHASE are liquid-only.
         * DEBT_PAYOFF gets no options (it isn't an account — uses the debt
         * fields instead). OTHER gets everything.
         */
        fun optionsFor(category: GoalCategory): List<GoalAccountType> = when (category) {
            GoalCategory.EMERGENCY_FUND,
            GoalCategory.VACATION,
            GoalCategory.MAJOR_PURCHASE,
            -> listOf(HISA, TFSA, CHEQUING)

            GoalCategory.HOME_PURCHASE -> listOf(FHSA, RRSP, TFSA, HISA, NON_REGISTERED)

            GoalCategory.RETIREMENT -> listOf(RRSP, TFSA, EMPLOYER_PENSION, NON_REGISTERED, HISA)

            GoalCategory.EDUCATION -> listOf(RESP, RRSP, TFSA, NON_REGISTERED)

            GoalCategory.INVESTMENT -> listOf(TFSA, NON_REGISTERED, RRSP, FHSA, HISA)

            // Debt-payoff goals don't have an "account" — they use the
            // mortgageInterestRate / mortgageRemainingBalance / mortgageRemainingMonths
            // fields on SavingsGoal instead.
            GoalCategory.DEBT_PAYOFF -> emptyList()

            GoalCategory.OTHER -> entries.toList()
        }
    }
}
